import { useCallback, useEffect, useRef, useState } from 'react';
import type { CreatedUploadDto, CreateUploadInput, FileDto } from '@cw/shared';
import { api, ApiError } from './api';

export type UploadStatus = 'preparing' | 'uploading' | 'finishing' | 'done' | 'error' | 'canceled';

export type UploadItem = {
  id: string;
  file: File;
  status: UploadStatus;
  /** 0 a 1 */
  progress: number;
  error?: string;
  result?: FileDto;
};

type Target = Omit<CreateUploadInput, 'name' | 'sizeBytes' | 'mimeType'>;

const PART_CONCURRENCY = 4;
const PART_RETRIES = 3;

/** PUT direto no armazenamento com XHR (fetch ainda não informa progresso de envio). Devolve o ETag. */
function put(
  url: string,
  headers: Record<string, string>,
  body: Blob,
  onProgress: (loaded: number) => void,
  signal: AbortSignal,
): Promise<string> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) return reject(new DOMException('cancelado', 'AbortError'));
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    for (const [k, v] of Object.entries(headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded);
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve(xhr.getResponseHeader('ETag') ?? '')
        : reject(new Error(`Falha no envio (${xhr.status})`));
    xhr.onerror = () => reject(new Error('Conexão interrompida durante o envio'));
    xhr.onabort = () => reject(new DOMException('cancelado', 'AbortError'));
    signal.addEventListener('abort', () => xhr.abort(), { once: true });
    xhr.send(body);
  });
}

/** Envia os bytes conforme o modo pedido pelo servidor: PUT único ou partes paralelas (com nova tentativa por parte). */
async function sendBytes(
  created: CreatedUploadDto,
  file: File,
  onProgress: (p: number) => void,
  signal: AbortSignal,
): Promise<{ partNumber: number; etag: string }[] | undefined> {
  if (created.mode === 'single') {
    await put(created.uploadUrl, created.headers, file, (l) => onProgress(l / file.size), signal);
    return undefined;
  }
  const partSize = created.partSize!;
  const count = created.partCount!;
  const loaded = new Array<number>(count).fill(0);
  const report = () => onProgress(loaded.reduce((a, b) => a + b, 0) / file.size);
  const done: { partNumber: number; etag: string }[] = [];
  const queue = Array.from({ length: count }, (_, i) => i + 1);

  async function worker() {
    while (queue.length) {
      // Pede URLs em lotes (válidas por 1 h).
      const batch = queue.splice(0, 5);
      const urls = await api.post<{ partNumber: number; url: string }[]>(`/files/${created.fileId}/parts`, {
        versionId: created.versionId,
        partNumbers: batch,
      });
      for (const { partNumber, url } of urls) {
        const blob = file.slice((partNumber - 1) * partSize, Math.min(file.size, partNumber * partSize));
        let attempt = 0;
        for (;;) {
          try {
            const etag = await put(url, {}, blob, (l) => ((loaded[partNumber - 1] = l), report()), signal);
            done.push({ partNumber, etag });
            break;
          } catch (e) {
            if (e instanceof DOMException || ++attempt >= PART_RETRIES) throw e;
            loaded[partNumber - 1] = 0;
            await new Promise((r) => setTimeout(r, 800 * attempt));
          }
        }
      }
    }
  }
  try {
    await Promise.all(Array.from({ length: Math.min(PART_CONCURRENCY, count) }, worker));
  } catch (e) {
    await api.post(`/files/${created.fileId}/abort`, { versionId: created.versionId }).catch(() => undefined);
    throw e;
  }
  return done;
}

/** Vídeo: captura um quadro no próprio navegador e envia como miniatura (o servidor não roda ffmpeg). */
async function sendVideoThumbnail(fileId: string, file: File): Promise<void> {
  if (!file.type.startsWith('video/') || file.size > 4 * 1024 ** 3) return;
  const url = URL.createObjectURL(file);
  try {
    const video = document.createElement('video');
    video.muted = true;
    video.preload = 'metadata';
    video.src = url;
    await new Promise<void>((resolve, reject) => {
      video.onloadeddata = () => resolve();
      video.onerror = () => reject(new Error('vídeo não suportado pelo navegador'));
      setTimeout(() => reject(new Error('tempo esgotado')), 8000);
    });
    video.currentTime = Math.min(1, (video.duration || 2) / 3);
    await new Promise((r) => (video.onseeked = r));
    const scale = Math.min(1, 480 / Math.max(video.videoWidth, video.videoHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(video.videoWidth * scale);
    canvas.height = Math.round(video.videoHeight * scale);
    canvas.getContext('2d')!.drawImage(video, 0, 0, canvas.width, canvas.height);
    const blob = await new Promise<Blob | null>((r) => canvas.toBlob(r, 'image/webp', 0.75));
    if (!blob) return;
    const { uploadUrl, headers } = await api.post<{ uploadUrl: string; headers: Record<string, string> }>(
      `/files/${fileId}/thumbnail`,
    );
    await put(uploadUrl, headers, blob, () => {}, new AbortController().signal);
    await api.post(`/files/${fileId}/thumbnail/complete`);
  } catch {
    /* sem miniatura: a interface mostra o ícone do tipo */
  } finally {
    URL.revokeObjectURL(url);
  }
}

/** Envia um arquivo novo: pede a URL assinada, envia direto ao armazenamento e confirma. */
export async function uploadFile(
  file: File,
  target: Target,
  onProgress: (p: number) => void = () => {},
  signal = new AbortController().signal,
): Promise<FileDto> {
  const created = await api.post<CreatedUploadDto>('/files/uploads', {
    ...target,
    name: file.name,
    sizeBytes: file.size,
    mimeType: file.type || 'application/octet-stream',
  });
  const parts = await sendBytes(created, file, onProgress, signal);
  const result = await api.post<FileDto>(`/files/${created.fileId}/complete`, {
    versionId: created.versionId,
    ...(parts ? { parts } : {}),
  });
  void sendVideoThumbnail(created.fileId, file);
  return result;
}

/** Envia uma nova versão (V2, V3…) de um arquivo existente. A anterior continua guardada. */
export async function uploadVersion(
  fileId: string,
  file: File,
  comment: string | undefined,
  onProgress: (p: number) => void = () => {},
  signal = new AbortController().signal,
): Promise<FileDto> {
  const created = await api.post<CreatedUploadDto>(`/files/${fileId}/versions`, {
    name: file.name,
    sizeBytes: file.size,
    mimeType: file.type || 'application/octet-stream',
    ...(comment ? { comment } : {}),
  });
  const parts = await sendBytes(created, file, onProgress, signal);
  const result = await api.post<FileDto>(`/files/${fileId}/complete`, {
    versionId: created.versionId,
    ...(parts ? { parts } : {}),
  });
  void sendVideoThumbnail(fileId, file);
  return result;
}

/**
 * Fila de envios com progresso, cancelamento e nova tentativa (SPEC §30).
 * Envia até 3 arquivos em paralelo.
 */
export function useUploadQueue(target: Target, onDone?: (f: FileDto) => void) {
  const [items, setItems] = useState<UploadItem[]>([]);
  const controllers = useRef(new Map<string, AbortController>());
  // Sempre o alvo e o callback mais recentes, sem recriar as funções da fila.
  const latest = useRef({ target, onDone });
  useEffect(() => {
    latest.current = { target, onDone };
  });
  const patch = (id: string, p: Partial<UploadItem>) =>
    setItems((list) => list.map((i) => (i.id === id ? { ...i, ...p } : i)));

  const run = useCallback(async (item: UploadItem) => {
    const ctrl = new AbortController();
    controllers.current.set(item.id, ctrl);
    patch(item.id, { status: 'preparing', progress: 0, error: undefined });
    try {
      const result = await uploadFile(
        item.file,
        latest.current.target,
        (p) => patch(item.id, { status: p >= 1 ? 'finishing' : 'uploading', progress: p }),
        ctrl.signal,
      );
      patch(item.id, { status: 'done', progress: 1, result });
      latest.current.onDone?.(result);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') patch(item.id, { status: 'canceled' });
      else
        patch(item.id, {
          status: 'error',
          error: e instanceof ApiError || e instanceof Error ? e.message : 'Falha no envio',
        });
    } finally {
      controllers.current.delete(item.id);
    }
  }, []);

  const add = useCallback(
    (files: FileList | File[]) => {
      const fresh = Array.from(files).map<UploadItem>((file) => ({
        id: crypto.randomUUID(),
        file,
        status: 'preparing',
        progress: 0,
      }));
      setItems((list) => [...fresh, ...list]);
      void (async () => {
        for (let i = 0; i < fresh.length; i += 3) await Promise.all(fresh.slice(i, i + 3).map(run));
      })();
    },
    [run],
  );

  const cancel = (id: string) => controllers.current.get(id)?.abort();
  const retry = (id: string) => {
    const item = items.find((i) => i.id === id);
    if (item) void run(item);
  };
  const dismiss = (id: string) => setItems((list) => list.filter((i) => i.id !== id));
  const clearFinished = () =>
    setItems((list) => list.filter((i) => i.status !== 'done' && i.status !== 'canceled'));

  return { items, add, cancel, retry, dismiss, clearFinished };
}

export function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  const units = ['KB', 'MB', 'GB'];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toLocaleString('pt-BR', { maximumFractionDigits: v < 10 ? 1 : 0 })} ${units[i]}`;
}
